import React, { useEffect, useRef, useState } from 'react';
import { ArrowRepeat, CheckCircle, ExclamationCircle, SlashCircle } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { relabelProduct } from '../api/brands';
import { describeLabeling } from './productLabeling';
import './LabelingBadge.scss';

// documents#188 — the small AI-labelling badge on a product card.
//
//   PENDING  "Labelling in progress…" with a pulsing dot
//   DONE     "Labelled: <category>" (or just "Labelled")
//   FAILED   "Labelling failed" + Retry, when the user may edit products
//   SKIPPED  "Cannot be labelled"
//   null     nothing (a row from before the labelling queue existed)
//
// Retry POSTs /brands/:id/products/:pid/relabel (embedded: its /shopify/admin
// twin). On 202 the parent gets the response's ai_labeling — PENDING — through
// `onRelabeled(productId, aiLabeling)` and flips the card at once, which also
// starts the tab's poll loop. A 409 (deleted, or no Shopify data to label
// from) shows a short note under the badge; any other failure a generic one.
export default function LabelingBadge({ product, brandId, canRelabel = false, onRelabeled }) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const view = describeLabeling(product);
  if (!view) return null;

  async function retry() {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const res = await relabelProduct(brandId, product.id);
      if (!alive.current) return;
      const next = res?.ai_labeling || { ...(product.ai_labeling || {}), status: 'PENDING' };
      if (onRelabeled) onRelabeled(product.id, next);
    } catch (err) {
      if (!alive.current) return;
      setErrorKey(err?.status === 409 ? 'products.labelNotLabelable' : 'products.labelRetryFailed');
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  const Icon = view.variant === 'done'
    ? CheckCircle
    : view.variant === 'failed'
      ? ExclamationCircle
      : view.variant === 'skipped'
        ? SlashCircle
        : null;

  return (
    <div className={`label-badge label-badge--${view.variant}`} data-status={view.status}>
      <span className="label-badge__pill" role="status">
        {view.variant === 'pending'
          ? <i className="label-badge__dot" aria-hidden="true" />
          : Icon && <Icon size={12} aria-hidden="true" />}
        <span className="label-badge__text">{t(view.key, view.vars)}</span>
        {view.retry && canRelabel && (
          <button
            type="button"
            className="label-badge__retry"
            onClick={retry}
            disabled={busy}
            aria-label={t('products.labelRetry')}
          >
            <ArrowRepeat size={12} className={busy ? 'label-badge__spin' : undefined} aria-hidden="true" />
            {busy ? t('products.labelRetrying') : t('products.labelRetry')}
          </button>
        )}
      </span>
      {errorKey && <div className="label-badge__error">{t(errorKey)}</div>}
    </div>
  );
}
